<template>
  <cms-collection :class="{ 'b-b': bottomBorder }" :content="mappedContent" />
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { ButtonVariant } from '#types/components/vf/button'
import type { Target } from '#types/target'
import { queryParamsToPreserve } from '#content/middleware/preserveQuery.global'

const { content } = defineProps<{ content: any }>()

const { bottomBorder, highlightButton } = content

const route = useRoute()

const activeButtonMapping = {
  'primary': 'secondary',
  'primary-inverse': 'secondary-inverse',
  'secondary': 'primary',
  'secondary-inverse': 'primary-inverse',
  'tertiary': 'primary',
  'tertiary-inverse': 'primary-inverse',
}

const addTextLinkStyles = (targets, content) => targets.map((target) => ({ ...target, ...getTextLinkStyles(content) }))

const selectActiveButton = (targets: Target[]) => {
  if (!highlightButton) return targets

  const sortedTargets = targets.toSorted((a, b) => (b.url?.length || 0) - (a.url?.length || 0))

  for (const target of sortedTargets) {
    if (!target.url || !target.style || !target.url.startsWith('/')) continue

    const [path, query] = target.url.split('?')

    if (route.path !== path) continue

    const { filters, ...queryObj } = Object.fromEntries(new URLSearchParams(query).entries())

    queryParamsToPreserve.forEach((k) => delete queryObj[k])

    if (filters) {
      // PLP filters require special handling, since we need to check nested stringified object
      if (!route.query.filters) continue
      const filtersObj = JSON.parse(filters)
      const routeFiltersObj = JSON.parse(route.query.filters.toString())
      if (!matchDeep(routeFiltersObj, filtersObj)) continue
    }

    if (!matchDeep(route.query, queryObj)) continue

    target.textLinkUnderline = 'always'

    target.style = target.style && Object.entries(target.style).reduce((acc, [key, value]) => ({
      ...acc,
      [key]: activeButtonMapping[value] || value
    }), {} as Responsive<ButtonVariant>)

    break
  }

  return targets
}

const getSlideMaxWidthForBreakpoint = (br: string) => {
  if (content.breakpoints[br].type === 'carousel')
    return br === 'sm' ? '19.25rem' : '26.5rem'

  return null
}

const mappedContent = ref()

const remapData = () => {
  mappedContent.value = {
    ...content,
    breakpoints: Object.assign({}, ...Object.keys(content.breakpoints).map((br) => ({
      [br]: {
        ...content.breakpoints[br],
        slidePadding: content.breakpoints[br].slidePadding ?? 'small',
        showArrows: br !== 'sm',
        slideMaxWidth: getSlideMaxWidthForBreakpoint(br)
      }
    }))),
    items: selectActiveButton(addTextLinkStyles(content.targets || [], content)).map((target) => ({
      type: 'VfCanvasCTAButton',
      target: {
        ...target,
        size: {
          sm: {
            size: target.size?.sm?.size ?? 'sm',
            fullWidth: true
          },
          md: {
            size: target.size?.md?.size ?? 'md'
          },
          lg: {
            size: target.size?.lg?.size ?? 'md'
          }
        }
      }
    }))
  }
}

remapData()

highlightButton && watch(() => route.fullPath, remapData)
</script>
